import React, { useState } from 'react';
import { signup } from '../../services';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { changeLoading } from '../../store';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import infoDanger from '../../assets/img/custom/info-danger.svg';
import logo from '../../assets/img/logo_kisahruang.png';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './styles.css';

const Register = () => {
  const dispatch = useDispatch();
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Name is required.')
      .min(3, 'Minimum 3 characters.')
      .max(50, 'Maximum 50 characters.')
      .matches(
        /^[a-zA-z]+[a-zA-Z ]*$/,
        'The first character cannot be empty. Only a combination of letters and spaces is allowed.'
      ),
    email: Yup.string()
      .required('Email is required.')
      .email('Please enter a valid email address.'),
    password: Yup.string().required('Password is required.'),
    confirmPassword: Yup.string()
      .required('Confirm password is required.')
      .oneOf(
        [Yup.ref('password')],
        'Confirm password must match the password.'
      ),
  });
  const formOptions = { resolver: yupResolver(validationSchema) };
  const [errorMessage, setErrorMessage] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm(formOptions);

  const handlePassword = (type) => {
    if (type === 'password') {
      setShowPassword(!showPassword);
      return;
    }

    setShowConfirmPassword(!showConfirmPassword);
  };

  const onSubmit = async ({ name, email, password, confirmPassword }, e) => {
    e.preventDefault();

    try {
      dispatch(changeLoading(true));
      const payload = {
        name: name,
        email: email,
        password: password,
        confirm_password: confirmPassword,
      };

      const response = await signup(payload);
      if (response && response.message === 'Success, please check your mail') {
        setSubmitted(true);
        toast.success('Success, please check your mail');
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message ?? 'Something went wrong');
    } finally {
      dispatch(changeLoading(false));
    }
  };

  return (
    <main className="register-container">
      <div className="register--form">
        <header className="mb-3">
          <div className="register--header-logo-container mb-3">
            <img
              src={logo}
              alt="app-logo"
              className="register--header-logo-img"
            />
          </div>
          {submitted ? null : (
            <h2 className="pe-1 text-gray fs-3 text-center fw-bold">
              Register
            </h2>
          )}
        </header>
        {submitted ? (
          <>
            <h3 className="text-gray text-center fw-bold">
              Please check your email
            </h3>
            <div className="d-flex align-items-center justify-content-center mt-3 fz-14 ask">
              <span className="pe-1">kembali ke halaman</span>
              <span className="">
                <Link
                  to="/"
                  className="link-dark fw-semibold btn-fill px-3 py-1 rounded"
                >
                  Login
                </Link>
              </span>
            </div>
          </>
        ) : (
          <form className="w-100" onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-3">
              <label className="form-label fz-12 fw-bold">Name</label>
              <input
                className="form-control border-input-default"
                {...register('name')}
                type="text"
                autocomplete="off"
              />
              {errors.name && (
                <div className="invalid-field">{errors.name?.message}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fz-12 fw-bold">Email</label>
              <input
                className="form-control border-input-default"
                {...register('email', {
                  required: true,
                  pattern: /^\S+@\S+$/i,
                })}
                type="text"
                autocomplete="off"
              />
              {errors.email && (
                <div className="invalid-field">{errors.email?.message}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fz-12 fw-bold">Password</label>
              <div className="d-flex border bg-white rounded password">
                <input
                  {...register('password')}
                  className="form-control border-0"
                  type={showPassword ? 'text' : 'password'}
                  aria-label="Username"
                  aria-describedby="basic-addon1"
                  autocomplete="off"
                />
                <span
                  className="input-group-text bg-white border-0"
                  id="basic-addon1"
                  onClick={() => handlePassword('password')}
                >
                  {showPassword ? (
                    <FaEyeSlash className="text-secondary" />
                  ) : (
                    <FaEye className="text-secondary" />
                  )}
                </span>
              </div>
              {errors.password && (
                <div className="invalid-field">{errors.password?.message}</div>
              )}
            </div>

            <div className="mb-5">
              <label className="form-label fz-12 fw-bold">
                Confirm Password
              </label>
              <div className={`d-flex border bg-white rounded password`}>
                <input
                  {...register('confirmPassword')}
                  className="form-control border-0"
                  type={showConfirmPassword ? 'text' : 'password'}
                  aria-label="Confirm Password"
                  aria-describedby="basic-addon1"
                  autocomplete="off"
                />
                <span
                  className="input-group-text bg-white border-0"
                  id="basic-addon1"
                  onClick={() => handlePassword('confirm_password')}
                >
                  {showConfirmPassword ? (
                    <FaEyeSlash className="text-secondary" />
                  ) : (
                    <FaEye className="text-secondary" />
                  )}
                </span>
              </div>
              {errors.confirmPassword && (
                <div className="invalid-field">
                  {errors.confirmPassword?.message}
                </div>
              )}
            </div>

            {errorMessage && (
              <div
                className="text-danger alert alert-danger d-flex align-items-center py-2 border-0"
                role="alert"
              >
                <img src={infoDanger} style={{ width: '23px' }} alt="warning" />
                <div className="fz-14 ps-2">{errorMessage}</div>
              </div>
            )}

            <button type="submit" className="btn w-100 fw-semibold btn-fill">
              Register
            </button>
          </form>
        )}
      </div>
    </main>
  );
};

export default Register;
